import React, {useMemo} from 'react';
import {Drawer} from 'components/customer/Product/Drawer/Drawer';
import {Col, Row} from 'react-bootstrap';
import type {Field as FieldType} from 'components/customer/Product/entity/Field';
import type {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {useRecalculateFaceHeight} from 'components/customer/Product/Drawer/helpers/useRecalculateFaceHeight';
import {DrawerType} from 'components/customer/Product/Drawer/DrawerType';
import {DrawerRunner as DrawerRunnerComponent} from 'components/customer/Product/Drawer/DrawerRunner';
import {useProductContext} from 'contexts/ProductContext';
import {useDrawerAmount} from 'components/customer/Product/Drawer/helpers/useDrawerAmount';
import {useInnerDrawerPosition} from 'components/customer/Product/Drawer/helpers/useInnerDrawerPosition';
import {DrawerLayout} from 'components/customer/Product/Drawer/DrawerLayout';
import {useDrawerParams} from 'components/customer/Product/Drawer/helpers/useDrawerParams';
interface DrawerRunnerType {
    field: FieldType;
    fieldset: Fieldset;
    reducedLabelColumnWidth?: boolean;
}

export const DrawerRunner = ({
    field,
    fieldset,
    reducedLabelColumnWidth = false,
}: DrawerRunnerType): JSX.Element => {
    const {isMiniBrowser} = useProductContext<{isMiniBrowser: boolean}>();

    const drawerParams = useDrawerParams(field, fieldset);
    const {drawerAmount} = useDrawerAmount(fieldset);

    useRecalculateFaceHeight({
        isInnerDrawer: drawerParams.isInnerDrawer,
        isDrawerComponent: drawerParams.isDrawerComponent,
        drawerGapFieldName: drawerParams.drawerGapFieldName,
        totalDrawerHeight: drawerParams.totalDrawerHeight,
        drawerTopMarginFieldName: drawerParams.drawerTopMarginFieldName,
        drawerBottomMarginFieldName: drawerParams.drawerBottomMarginFieldName,
    });

    useInnerDrawerPosition(drawerParams.isInnerDrawer);

    return useMemo<JSX.Element>(() => {
        if (drawerParams) {
            if (drawerParams.isDrawerComponent && !drawerParams.isInnerDrawer) {
                let labelColSpan = reducedLabelColumnWidth ? 4 : 5;
                if (isMiniBrowser) {
                    labelColSpan = 6;
                }

                return (
                    <Row>
                        <Col xs={isMiniBrowser ? 12 : 8}>
                            <DrawerType
                                isDrawerComponent={
                                    drawerParams.isDrawerComponent
                                }
                                fieldName={drawerParams.drawerTypeFieldName}
                                labelColSpan={labelColSpan}
                            />
                            <DrawerRunnerComponent
                                isDrawerComponent={
                                    drawerParams.isDrawerComponent
                                }
                                fieldName={drawerParams.drawerRunnerFieldName}
                                drawerTypeFieldName={
                                    drawerParams.drawerTypeFieldName
                                }
                                drawerFaceHeightFieldName={
                                    drawerParams.drawerFaceHeightFieldName
                                }
                                fingerPullFieldName={
                                    drawerParams.fingerPullFieldName
                                }
                                drawerDepth={drawerParams.maxRunnerDepth}
                                labelColSpan={labelColSpan}
                            />
                        </Col>
                    </Row>
                );
            }

            // inner drawers keep the row per drawer layout: they have no face
            // heights to draw, only positions and clearances
            if (drawerParams.isInnerDrawer) {
                return (
                    <>
                        {Array.from(Array(drawerAmount).keys()).map((index) => {
                            return (
                                <Drawer
                                    key={index}
                                    runnerIndex={index}
                                    drawerParams={drawerParams}
                                    showLine={drawerAmount !== index + 1}
                                />
                            );
                        })}
                    </>
                );
            }

            return (
                <DrawerLayout
                    drawerParams={drawerParams}
                    drawerAmount={drawerAmount}
                />
            );
        }

        return null;
    }, [drawerParams, drawerAmount]);
};
